/** W75 (Nachtrag W8, plan-w75.md 2.9): Platzhalter in eckigen Klammern als Felder. Erkannt wie `briefpdf.PLATZHALTER`
 *  (hoechstens 60 Zeichen, D-260 H1); Beschriftung und Typ aus der letzten Antwort des Servers, unbekannte als Text. */
import type { PlatzhalterInfo } from "../../api/typen";
import { betrag, betragEingabe, datum } from "../../format";

export const PLATZHALTER = /\[[^[\]\n]{1,60}\]/g;
/** Der Platzhalter fuer den vollen Namen (Server `NAME_FEHLT`); W73e (D-380 HB9): sein Chip fuehrt ins Blatt „Absender“, der
 *  Server fuellt ihn aus dem vollen Namen des Absenders. */
export const NAME_FEHLT = "[Vollständiger Name des Absenders]";
const VORBELEGT = /^\[(\d{2})\.(\d{2})\.(\d{4}), bitte prüfen\]$/;

/** Die offenen Platzhalter aus Betreff und Text in der Reihenfolge des ersten Vorkommens, jeder einmal; nach dem
 *  Tippen lokal neu berechnet, die Angaben dazu aus `bekannt` (Brief und Vorlage). */
export function offenePlatzhalter(betreff: string, text: string, bekannt: readonly PlatzhalterInfo[]): PlatzhalterInfo[] {
  const namen = [...new Set(`${betreff}\n${text}`.match(PLATZHALTER) ?? [])];
  return namen.map((name) => {
    const info = bekannt.find((p) => p.name === name);
    if (info) return info;
    const tag = VORBELEGT.exec(name);
    if (tag) {
      const muster = bekannt.find((p) => p.vorbelegt && p.typ === "datum");
      return { name, beschriftung: muster?.beschriftung ?? "Datum, bitte prüfen", typ: "datum", vorbelegt: `${tag[1]}.${tag[2]}.${tag[3]}` };
    }
    return { name, beschriftung: name.slice(1, -1).trim(), typ: "text" };
  });
}

/** Jedes Vorkommen des Platzhalters durch den Wert ersetzen (gleicher Name, gleiche Sache). */
export function ersetzen(text: string, name: string, wert: string): string {
  return text.split(name).join(wert);
}

/** Wahl „Satz behalten“ entfernt nur die Zeile des Platzhalters, „Satz streichen“ auch die folgende Zeile. */
export function waehlen(text: string, name: string, behalten: boolean): string {
  const zeilen = text.split("\n");
  const aus: string[] = [];
  for (let i = 0; i < zeilen.length; i++) {
    if (zeilen[i]?.includes(name)) {
      if (!behalten) i++;
      continue;
    }
    aus.push(zeilen[i] ?? "");
  }
  return aus.join("\n");
}

/** Schutzhinweis entfernen: die Klammer weg; bleibt die Zeile leer, faellt sie weg (keine doppelte Leerzeile). */
export function hinweisEntfernen(text: string, name: string): string {
  const aus: string[] = [];
  for (const zeile of text.split("\n")) {
    if (!zeile.includes(name)) {
      aus.push(zeile);
      continue;
    }
    const rest = ersetzen(zeile, name, "").replace(/ {2,}/g, " ").trimEnd();
    if (rest.trim()) aus.push(rest);
  }
  return aus.join("\n").replace(/\n{3,}/g, "\n\n");
}

/** Der Wert, der in den Brief kommt: Datum „TT.MM.JJJJ“, Betrag „50,00 €“; null, wenn er nicht passt. */
export function ausgabe(typ: PlatzhalterInfo["typ"], eingabe: string): string | null {
  const roh = eingabe.trim();
  if (!roh) return null;
  if (typ === "datum") return /^\d{4}-\d{2}-\d{2}$/.test(roh) ? datum(roh) : null;
  if (typ === "betrag") {
    const wert = betragEingabe(roh);
    return wert === null ? null : betrag(wert);
  }
  // Text und Nummer: ein Platzhalter im Betreff bleibt einzeilig; eckige Klammern waeren neue Platzhalter
  return roh.replace(/[[\]]/g, "");
}

/** „31.12.2027“ als Wert fuer das Datumsfeld („2027-12-31“). */
export function alsIso(tag: string | null | undefined): string {
  const t = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(tag ?? "");
  return t ? `${t[3]}-${t[2]}-${t[1]}` : "";
}
